import type { Me } from "@pcj/shared";
import { Building2, SearchX, TriangleAlert } from "lucide-react";
import { type FormEvent, useState } from "react";
import { isRouteErrorResponse, Link, Navigate, Outlet, useLocation, useNavigate, useRouteError } from "react-router";
import { AppHeader } from "../components/AppHeader";
import { Banner, Button, Card, EmptyState, Field, inputClass, PageLoader } from "../components/ui";
import { ApiError, errorMessage, issueFor } from "../lib/api";
import { useSignOut } from "../lib/auth-client";
import { useMe, useRegisterCompany } from "../lib/queries";

/** Guards every signed-in route; sends visitors without a session to /login. */
export function RequireSession() {
  const me = useMe();
  const location = useLocation();
  const signOut = useSignOut();
  if (me.isPending) return <PageLoader label="Verificando sesión…" />;
  if (me.isError) {
    // Deactivation closes the member's sessions; this covers one that slipped through.
    const deactivated = me.error instanceof ApiError && me.error.code === "account_deactivated";
    return (
      <div className="mx-auto max-w-md space-y-3 p-6">
        <Banner tone="error" title={deactivated ? "Acceso desactivado" : "No pudimos conectar con el servidor"}>
          {errorMessage(me.error)}
        </Banner>
        {deactivated && (
          <Button variant="secondary" onClick={signOut}>
            Salir
          </Button>
        )}
      </div>
    );
  }
  if (!me.data) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** The signed-in user. Only valid below <RequireSession>. */
export function useCurrentUser(): Me {
  const { data } = useMe();
  if (!data) throw new Error("useCurrentUser used outside <RequireSession>");
  return data;
}

/** Where each role lands after signing in. */
export function homePath(me: Me): string {
  if (me.profile.role === "chamber_admin") return "/chamber";
  if (me.profile.companyId) return `/companies/${me.profile.companyId}`;
  return "/onboarding";
}

export function HomeRedirect() {
  return <Navigate to={homePath(useCurrentUser())} replace />;
}

/** First login of a jewelry store's compliance officer: register the company. */
export function OnboardingPage() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const register = useRegisterCompany();
  const [form, setForm] = useState({
    legalName: "",
    legalForm: "",
    rfc: "",
    taxAddress: "",
    complianceOfficer: me.profile.name ?? me.user.name,
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  if (me.profile.role !== "company_admin" || me.profile.companyId) return <Navigate to={homePath(me)} replace />;

  function submit(event: FormEvent) {
    event.preventDefault();
    register.mutate(form, { onSuccess: (company) => navigate(`/companies/${company.id}`, { replace: true }) });
  }
  const error = register.error;

  return (
    <div className="min-h-screen">
      <AppHeader me={me} title="Bienvenido al portal" />
      <main className="mx-auto max-w-lg px-4 py-8">
        <Card className="p-6">
          <div className="mb-5 flex items-start gap-3">
            <Building2 className="mt-0.5 h-6 w-6 text-gold-500" aria-hidden />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Registra tu joyería</h2>
              <p className="text-sm text-gray-500">
                Estos datos identifican a tu empresa como sujeto obligado. Solo tú y tu equipo verán su información.
              </p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Razón social" required error={issueFor(error, "legalName")}>
              {(id) => (
                <input id={id} required className={inputClass} value={form.legalName} onChange={set("legalName")} />
              )}
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Forma jurídica" error={issueFor(error, "legalForm")}>
                {(id) => (
                  <input
                    id={id}
                    placeholder="S.A. de C.V."
                    className={inputClass}
                    value={form.legalForm}
                    onChange={set("legalForm")}
                  />
                )}
              </Field>
              <Field label="RFC" error={issueFor(error, "rfc")}>
                {(id) => <input id={id} className={`${inputClass} uppercase`} value={form.rfc} onChange={set("rfc")} />}
              </Field>
            </div>
            <Field label="Domicilio fiscal" error={issueFor(error, "taxAddress")}>
              {(id) => <input id={id} className={inputClass} value={form.taxAddress} onChange={set("taxAddress")} />}
            </Field>
            <Field label="Responsable de Cumplimiento" error={issueFor(error, "complianceOfficer")}>
              {(id) => (
                <input
                  id={id}
                  className={inputClass}
                  value={form.complianceOfficer}
                  onChange={set("complianceOfficer")}
                />
              )}
            </Field>
            {error && !issueFor(error, "legalName") && <Banner tone="error">{errorMessage(error)}</Banner>}
            <Button type="submit" loading={register.isPending} className="w-full">
              Crear y entrar al portal
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <main className="mx-auto max-w-md px-4 py-16">
      <EmptyState icon={SearchX} title="Página no encontrada">
        <Link to="/" className="underline">
          Volver al inicio
        </Link>
      </EmptyState>
    </main>
  );
}

export function RouteErrorPage() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : errorMessage(error);
  return (
    <main className="mx-auto max-w-md px-4 py-16">
      <EmptyState icon={TriangleAlert} title="Algo salió mal">
        <p className="mb-3">{message}</p>
        <Button variant="secondary" onClick={() => window.location.assign("/")}>
          Recargar
        </Button>
      </EmptyState>
    </main>
  );
}
